import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ArrowRight, Calendar, Clock, FileText, Play, Search, Tag } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { blogPosts, formatDate } from "@/lib/blog-data";
import { videoItems } from "@/lib/video-data";

/** Surgery-specific topics for the blog filter chips.
 *  RegExes are intentionally strict so unrelated posts/videos don't leak
 *  into another topic (e.g. "urologist" shouldn't appear under Kidney Stones).
 */
const surgeryTopics: { label: string; match: RegExp }[] = [
  { label: "Piles", match: /\b(?:piles?|haemorrhoid|hemorrhoid)\b/i },
  { label: "Cataract", match: /\b(?:cataract|phaco|intraocular lens|iol)\b/i },
  { label: "Knee Replacement", match: /\b(?:knee replacement|knee arthroplasty|knee surgery|joint replacement)\b/i },
  { label: "Circumcision", match: /\b(?:circumcision|phimosis|zsr stapler)\b/i },
  { label: "Kidney Stones", match: /\b(?:kidney stones?|renal calculus|nephrolith|pcnl|rirs|ureteroscopy)\b/i },
  { label: "Childbirth", match: /\b(?:childbirth|pregnancy|pregnant|delivery|caesarean|c-section|antenatal|obstetric)\b/i },
  { label: "Varicose Veins", match: /\b(?:varicose veins?|evla|venous ulcer|spider veins?)\b/i },
  { label: "Tonsillitis", match: /\b(?:tonsillitis|tonsillectomy|tonsils?)\b/i },
];

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Health Blog — Healic Multispeciality Hospital" },
      {
        name: "description",
        content:
          "Expert health articles from Healic specialists — surgery guides, recovery tips, women's health, urology, ENT, ophthalmology and more.",
      },
      { property: "og:title", content: "Healic Health Blog" },
      {
        property: "og:description",
        content: "Trusted health insights from Healic's expert doctors.",
      },
      { property: "og:url", content: "https://healic.in/blog" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://healic.in/og-image.jpg" },
      { property: "og:image:alt", content: "Healic Health Blog — expert medical advice" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Healic Health Blog" },
      { name: "twitter:description", content: "Trusted health insights from Healic's expert doctors." },
      { name: "twitter:image", content: "https://healic.in/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://healic.in/blog" }],
  }),
  component: BlogIndex,
});

function BlogIndex() {
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState<string>("All");

  const featured = blogPosts.find((p) => p.featured) ?? blogPosts[0];
  const rest = blogPosts.filter((p) => p.slug !== featured.slug);

  const topic = surgeryTopics.find((t) => t.label === cat);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rest.filter((p) => {
      if (topic && !topic.match.test(`${p.title} ${p.excerpt} ${p.tags.join(" ")} ${p.category}`))
        return false;
      if (q && !`${p.title} ${p.excerpt} ${p.tags.join(" ")}`.toLowerCase().includes(q))
        return false;
      return true;
    });
  }, [rest, query, cat, topic]);

  const filteredVideos = useMemo(() => {
    const q = query.trim().toLowerCase();
    return videoItems.filter((v) => {
      const hay = `${v.title} ${v.description} ${v.speaker} ${v.speakerRole}`;
      if (topic && !topic.match.test(hay)) return false;
      if (!q) return true;
      return hay.toLowerCase().includes(q);
    });
  }, [query, cat, topic]);

  return (
    <PageShell>
      <div className="bg-muted/40">
        {/* Hero */}
        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-7xl px-4 py-8 lg:px-8 lg:py-10">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-accent">
              Healic Health Journal
            </p>
            <h1 className="mt-2 font-display text-2xl font-bold sm:text-3xl lg:text-4xl">
              Expert advice from doctors you can trust
            </h1>
            <p className="mt-2 max-w-2xl text-sm opacity-85 lg:text-base">
              Recovery guides, prevention tips, surgical insights and patient stories — written by
              Healic specialists across general surgery, gynaecology, urology, ENT, ophthalmology
              and orthopaedics.
            </p>
          </div>
        </section>

        {/* Featured */}
        <section className="mx-auto max-w-7xl px-4 pt-6 lg:px-8 lg:pt-8">
          <Link
            to="/blog/$slug"
            params={{ slug: featured.slug }}
            className="group grid overflow-hidden rounded-2xl border border-border bg-card shadow-soft transition hover:shadow-card lg:grid-cols-[1.1fr_1fr]"
          >
            <div className="relative aspect-[16/9] overflow-hidden lg:aspect-auto lg:max-h-[280px]">
              <img
                src={featured.cover}
                alt={featured.title}
                width={1200}
                height={630}
                className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                loading="eager"
              />
              <span className="absolute left-3 top-3 rounded-pill bg-accent px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-accent-foreground">
                Featured
              </span>
            </div>
            <div className="flex flex-col justify-center gap-2.5 p-5 sm:p-6">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-accent">
                {featured.category}
              </p>
              <h2 className="font-display text-xl font-bold leading-tight sm:text-2xl group-hover:text-accent">
                {featured.title}
              </h2>
              <p className="line-clamp-2 text-sm text-muted-foreground">{featured.excerpt}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3 w-3" /> {formatDate(featured.date)}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="h-3 w-3" /> {featured.readMinutes} min read
                </span>
                <span>By {featured.author}</span>
              </div>
              <span className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-accent">
                Read article <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
              </span>
            </div>
          </Link>
        </section>

        {/* Filters */}
        <section className="mx-auto max-w-7xl px-4 pt-6 lg:px-8">
          <div className="rounded-2xl border border-border bg-card p-3 shadow-soft sm:p-4">
            <div className="grid gap-2.5 lg:grid-cols-[1.6fr_auto]">
              <label className="relative flex items-center">
                <Search className="absolute left-3.5 h-4 w-4 text-muted-foreground" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search surgery topics, articles and videos…"
                  className="w-full rounded-pill border border-border bg-background py-2.5 pl-10 pr-4 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
                  aria-label="Search surgery topics, articles and videos"
                />
              </label>
              <div className="flex flex-wrap gap-1.5">
                {["All", ...surgeryTopics.map((t) => t.label)].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCat(c)}
                    className={`rounded-pill px-3 py-1.5 text-[11px] font-semibold transition ${
                      cat === c
                        ? "bg-primary text-primary-foreground"
                        : "bg-cream text-foreground hover:bg-accent/15"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Grid — articles and videos together */}
        <section className="mx-auto max-w-7xl px-4 py-6 pb-12 lg:px-8 lg:py-8">
          {filtered.length === 0 && filteredVideos.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
              <h2 className="font-display text-xl font-semibold">
                Nothing matches your filters
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Try a different keyword or category.
              </p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.map((p) => (
                <article
                  key={p.slug}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition hover:shadow-card"
                >
                  <Link
                    to="/blog/$slug"
                    params={{ slug: p.slug }}
                    className="relative block aspect-[16/9] overflow-hidden"
                  >
                    <img
                      src={p.cover}
                      alt={p.title}
                      width={640}
                      height={360}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.05]"
                      loading="lazy"
                    />
                    <span className="absolute left-2.5 top-2.5 rounded-pill bg-background/90 px-2.5 py-0.5 text-[10px] font-semibold text-primary">
                      {p.category}
                    </span>
                  </Link>
                  <div className="flex flex-1 flex-col p-4">
                    <Link
                      to="/blog/$slug"
                      params={{ slug: p.slug }}
                      className="font-display text-base font-semibold leading-snug hover:text-accent"
                    >
                      {p.title}
                    </Link>
                    <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">{p.excerpt}</p>
                    <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> {formatDate(p.date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {p.readMinutes} min
                      </span>
                    </div>
                    <div className="mt-2.5 flex flex-wrap gap-1">
                      {p.tags.slice(0, 3).map((t) => (
                        <span
                          key={t}
                          className="inline-flex items-center gap-1 rounded-pill bg-cream px-2 py-0.5 text-[10px] font-semibold text-foreground"
                        >
                          <Tag className="h-2.5 w-2.5" /> {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </article>
              ))}

              {filteredVideos.map((v) => (
                <article
                  key={v.slug}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition hover:shadow-card"
                >
                  <Link
                    to="/blog/videos/$slug"
                    params={{ slug: v.slug }}
                    className="relative block aspect-[16/9] overflow-hidden bg-primary"
                  >
                    {v.poster ? (
                      <img
                        src={v.poster}
                        alt={v.title}
                        width={640}
                        height={360}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.05]"
                        loading="lazy"
                      />
                    ) : null}
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-soft transition group-hover:scale-110">
                        <Play className="h-5 w-5" />
                      </span>
                    </span>
                    <span className="absolute left-2.5 top-2.5 rounded-pill bg-background/90 px-2.5 py-0.5 text-[10px] font-semibold text-primary">
                      {v.kind === "doctor" ? "Doctor video" : "Patient story"}
                    </span>
                  </Link>
                  <div className="flex flex-1 flex-col p-4">
                    <Link
                      to="/blog/videos/$slug"
                      params={{ slug: v.slug }}
                      className="font-display text-base font-semibold leading-snug hover:text-accent"
                    >
                      {v.title}
                    </Link>
                    <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">
                      {v.description}
                    </p>
                    <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground">
                      <span>{v.speakerRole}</span>
                      <span className="flex items-center gap-1">
                        <FileText className="h-3 w-3" /> Transcript
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>


        {/* Newsletter CTA */}
        <section className="mx-auto max-w-7xl px-4 pb-12 lg:px-8">
          <div className="overflow-hidden rounded-2xl bg-primary p-6 text-primary-foreground sm:p-8">
            <div className="grid items-center gap-4 lg:grid-cols-[1.4fr_1fr]">
              <div>
                <h2 className="font-display text-xl font-bold sm:text-2xl">
                  Health tips, in your inbox
                </h2>
                <p className="mt-1.5 max-w-xl text-sm opacity-85">
                  One short, useful email a month from Healic doctors. No spam, unsubscribe anytime.
                </p>
              </div>
              <form
                onSubmit={(e) => e.preventDefault()}
                className="flex flex-col gap-2 sm:flex-row"
              >
                <input
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="w-full rounded-pill border border-white/20 bg-white/10 px-4 py-2.5 text-sm text-primary-foreground placeholder:text-primary-foreground/60 outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
                />
                <button
                  type="submit"
                  className="rounded-pill bg-accent px-5 py-2.5 text-sm font-semibold text-accent-foreground hover:opacity-90"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        </section>

      </div>
    </PageShell>
  );
}
